import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, Lock, Play, Calendar, LogOut, AlertTriangle, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_COMMUNITY_URL = "https://chat.whatsapp.com/JBaMWEZaZOJ1a1MjESmuUB";

// Convert ISO YYYY-MM-DD → DD-MM-YYYY (Indian display format)
function fmtDate(iso) {
  if (!iso || typeof iso !== "string") return iso || "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
}
// Convert Google Drive view/edit link to embed-friendly preview link
function toDrivePreview(link) {
  if (!link) return link;
  const m = link.match(/\/file\/d\/([^/]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  return link;
}

export default function CourseLoginPage() {
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [expiredMsg, setExpiredMsg] = useState("");
  const [wmCorner, setWmCorner] = useState(0); // 0=TL, 1=TR, 2=BR, 3=BL

  // Rotate corner watermark every 20s to prevent physical sticker cover-up
  useEffect(() => {
    if (!nowPlaying) return;
    const iv = setInterval(() => {
      setWmCorner((c) => (c + 1 + Math.floor(Math.random() * 3)) % 4);
    }, 20000);
    return () => clearInterval(iv);
  }, [nowPlaying]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!mobile.trim() || !email.trim()) {
      toast.error("Mobile aur Email dono zaruri hain");
      return;
    }
    setLoading(true);
    setExpiredMsg("");
    try {
      await axios.post(`${API}/student/login`, { mobile: mobile.trim(), email: email.trim() });
      const rec = await axios.post(`${API}/student/recordings`, { mobile: mobile.trim(), email: email.trim() });
      setStudent({
        name: rec.data.student_name,
        mobile: mobile.trim(),
        access_ends_on: rec.data.access_ends_on,
      });
      setRecordings(rec.data.recordings);
      toast.success(`Welcome ${rec.data.student_name}!`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed";
      const status = err?.response?.status;
      if (status === 403) {
        // Access expired — show inline renewal screen
        setExpiredMsg(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setStudent(null);
    setRecordings([]);
    setNowPlaying(null);
    setMobile("");
    setEmail("");
    setExpiredMsg("");
  };

  // Access expired screen
  if (expiredMsg) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center" data-testid="access-expired-screen">
          <div className="w-20 h-20 rounded-2xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-10 h-10 text-rose-400" />
          </div>
          <h1 className="text-3xl font-black text-white mb-3">Access Expired</h1>
          <p className="text-slate-300 text-sm mb-2">
            Aapka access expire ho gaya hai.
          </p>
          <p className="text-slate-500 text-xs mb-6">{expiredMsg}</p>
          <a
            href={WA_COMMUNITY_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="renew-wa-btn"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-lg shadow-[0_8px_20px_rgba(16,185,129,0.4)] transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            Renew karne ke liye WhatsApp par contact karein
          </a>
          <button
            onClick={handleLogout}
            className="block mx-auto mt-4 text-slate-500 hover:text-slate-300 text-xs"
            data-testid="back-to-login-btn"
          >
            ← Back to login
          </button>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-[0_0_40px_rgba(16,185,129,0.4)]">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-black text-white mb-2">Course Portal</h1>
            <p className="text-slate-400 text-sm">VEE Consultancy — Class Recordings Access</p>
          </div>

          <form onSubmit={handleLogin} className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur">
            <div className="mb-4">
              <label className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block">
                Mobile Number
              </label>
              <input
                type="tel"
                inputMode="numeric"
                data-testid="student-mobile-input"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="9876543210"
                className="w-full h-12 bg-slate-900/80 border border-slate-700 focus:border-emerald-500 text-white rounded-lg px-4 outline-none"
              />
            </div>
            <div className="mb-6">
              <label className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block">
                Email Address
              </label>
              <input
                type="email"
                data-testid="student-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full h-12 bg-slate-900/80 border border-slate-700 focus:border-emerald-500 text-white rounded-lg px-4 outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="student-login-btn"
              className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-bold rounded-lg shadow-[0_8px_20px_rgba(16,185,129,0.4)] disabled:opacity-50 transition-all"
            >
              {loading ? "Logging in..." : (
                <span className="inline-flex items-center justify-center gap-2">
                  <Lock className="w-4 h-4" /> Access Recordings
                </span>
              )}
            </button>
            <p className="text-[11px] text-slate-500 text-center mt-4">
              Sirf paid course students hi access kar sakte hain.
              <br />
              Mobile + Email dono match hone chahiye.
            </p>
          </form>
        </div>
      </div>
    );
  }

  // Watermark text for videos — brand + student identity (piracy deterrent)
  const watermarkText = `Vee Consultancy | Proprietary Content for ${student.name} - ${student.mobile}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white">
              🎓 Welcome, <span className="text-emerald-400">{student.name}</span>!
            </h1>
            <p className="text-slate-400 text-sm mt-1 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Access valid till: <span className="text-emerald-300 font-semibold">{fmtDate(student.access_ends_on)}</span>
            </p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="student-logout-btn"
            className="inline-flex items-center gap-2 h-10 px-4 bg-rose-600/20 hover:bg-rose-600/30 border border-rose-500/40 text-rose-300 rounded-lg text-sm font-semibold"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        {/* Now Playing */}
        {nowPlaying && (
          <div className="mb-6 rounded-2xl bg-black border border-white/10 overflow-hidden">
            <div className="px-4 py-3 flex items-center justify-between bg-slate-900/60">
              <h3 className="text-white font-bold text-sm md:text-base">
                🎬 {nowPlaying.title}
              </h3>
              <button
                onClick={() => setNowPlaying(null)}
                className="text-slate-400 hover:text-white text-sm"
                data-testid="close-player-btn"
              >
                ✕ Close
              </button>
            </div>
            {/* Video container with watermark overlay */}
            <div className="relative aspect-video bg-black">
              <iframe
                src={toDrivePreview(nowPlaying.video_link)}
                title={nowPlaying.title}
                className="w-full h-full absolute inset-0"
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture; accelerometer; gyroscope"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                loading="eager"
                data-testid="recording-iframe"
              />
              {/* Corner watermark — rotates between 4 corners every 20s (anti-sticker) */}
              <div
                className={`pointer-events-none absolute px-3 py-1 rounded bg-black/50 backdrop-blur-sm text-white/80 text-[10px] md:text-[11px] font-mono tracking-wide select-none max-w-[70%] transition-all duration-700 ease-in-out z-10 ${
                  wmCorner === 0 ? "top-3 left-3 text-left" :
                  wmCorner === 1 ? "top-3 right-3 text-right" :
                  wmCorner === 2 ? "bottom-3 right-3 text-right" :
                                   "bottom-3 left-3 text-left"
                }`}
                data-testid="video-watermark-corner"
              >
                {watermarkText}
              </div>
              {/* Diagonal floating watermark — anti-piracy, drifts across the frame */}
              <div
                className="pointer-events-none absolute inset-0 overflow-hidden select-none z-10"
                data-testid="video-watermark-drift"
              >
                <div className="wm-drift absolute text-white/20 text-sm md:text-lg font-bold whitespace-nowrap">
                  {watermarkText}
                </div>
              </div>
            </div>
            {/* Mobile fallback: if iframe playback fails, open in Drive app / new tab */}
            <div className="px-4 py-3 bg-slate-900/60 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
              <p className="text-slate-400">
                📱 Mobile pe video nahi chal raha? Ye button dabao ↓
              </p>
              <a
                href={toDrivePreview(nowPlaying.video_link)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-in-drive-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-md text-xs"
              >
                🔗 Open in Google Drive
              </a>
            </div>
          </div>
        )}

        {/* Recordings List */}
        <h2 className="text-white font-bold text-xl mb-4">📚 Class Recordings ({recordings.length})</h2>
        {recordings.length === 0 ? (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center text-slate-400">
            Abhi tak koi recording available nahi hai. Wait for the next class!
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {recordings.map((rec) => (
              <div
                key={rec.id}
                data-testid={`recording-card-${rec.id}`}
                className="rounded-xl bg-white/5 border border-white/10 hover:border-emerald-500/40 p-4 flex items-center gap-4 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                  <Play className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-semibold text-sm md:text-base truncate">{rec.title}</h4>
                  <p className="text-slate-500 text-xs">📅 {fmtDate(rec.class_date)}</p>
                </div>
                <button
                  onClick={() => {
                    setNowPlaying(rec);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  data-testid={`play-btn-${rec.id}`}
                  className="h-10 px-4 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg text-sm"
                >
                  ▶️ Watch
                </button>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-slate-600 text-xs mt-8">
          ⚠️ Recording download disabled — sirf watch access. Video par aapka Naam + Mobile watermark visible hai. Piracy pe legal action liya jayega.
        </p>
      </div>

      {/* Watermark drift animation styles */}
      <style>{`
        @keyframes wm-drift {
          0%   { transform: translate(5%, 15%) rotate(-8deg); opacity: 0.15; }
          25%  { transform: translate(60%, 30%) rotate(-8deg); opacity: 0.2; }
          50%  { transform: translate(20%, 65%) rotate(-8deg); opacity: 0.15; }
          75%  { transform: translate(55%, 80%) rotate(-8deg); opacity: 0.22; }
          100% { transform: translate(5%, 15%) rotate(-8deg); opacity: 0.15; }
        }
        .wm-drift {
          animation: wm-drift 22s ease-in-out infinite;
          top: 0; left: 0;
          text-shadow: 0 1px 3px rgba(0,0,0,0.5);
        }
      `}</style>
    </div>
  );
}
